var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { ArraySchema, Schema, type } from "@colyseus/schema";
/**
 * One of a player's implemented (face-up) Fortress cards. The catalogue entry
 * (pattern / cost / effect) is looked up by `cardId`; this carries only the
 * mutable per-card state.
 */
export class FortressCardState extends Schema {
    constructor() {
        super(...arguments);
        /** Catalogue card id. */
        this.cardId = "";
        /**
         * Tokens currently sitting on this card. Some Fortress effects let a player
         * place tokens here (e.g. to track charges / accumulated value).
         */
        this.tokens = 0;
        /**
         * Resources stored ON the card, separate from tokens — Bursted Flap piles up
         * the energy penalties it witnesses here and converts ≥10 into a token at
         * round end; Swole Turtle ("49"+) banks its round-end energy deposits here
         * (its tokens count the collect's uses).
         */
        this.stored = 0;
        /** True once the card is exhausted and its effect can no longer be used. */
        this.disabled = false;
        /**
         * Big Boom's rescale of this instance's token cap ("double or halve the
         * token capacity of another fortress you own"): 1 = doubled, -1 = halved,
         * 0 = the printed cap. Read through `instanceTokenCap`.
         */
        this.capScale = 0;
        /**
         * True on a phantom instance a CurdleCat AWARDED (its "Copy the effect of
         * another implemented fortress"): a full card for every rule — hooks,
         * passives, grants, win predicates, its own fresh tokens/stored state —
         * that simply never occupies board nodes. It leaves play when its
         * CurdleCat switches copies or is destroyed, and never returns to a pile.
         */
        this.copied = false;
        /**
         * On the COPIER (CurdleCat) instance: the card id of the copy it currently
         * has in play, or "" when none. The link switching and the copier's own
         * destruction use to find the awarded phantom ("only one copy at a time").
         */
        this.copiedCardId = "";
        /**
         * Card ids this fortress has MARKED (Dog Den: one win + one regular per
         * use of its mark action; a Tokenizer re-arming the action accumulates
         * more). Marks stay listed once made; empty on cards that never mark.
         */
        this.markedCardIds = new ArraySchema();
        /** Latched when anyone implements one of this card's marked WIN-deck
         * cards (Dog Den's win needs one mark per deck implemented). */
        this.markedWinImplemented = false;
        /** Latched when anyone implements one of this card's marked REGULAR-deck
         * cards. */
        this.markedRegularImplemented = false;
        /** Zone shared by the realised pattern, or -1 when it spans zones. */
        this.zoneLevel = -1;
        /**
         * Board node ids this fortress occupies (the buildings + markers that realise
         * its pattern), recorded when the card is implemented. Lets the client
         * highlight the fortress on the board and pan/zoom to it from the player
         * roster — the node schema only tracks `fortressOwnerId`, not which card a
         * node belongs to, so the link is stored here.
         */
        this.nodeIds = new ArraySchema();
        /** Bridge edges belonging to the pattern, as canonical `"lo-hi"` keys. */
        this.bridgeKeys = new ArraySchema();
        /**
         * Node the owner DESIGNATED as a forced attack target (Stab Pole Hole's
         * designate_attack_target action), or -1 when none. A defense held on this
         * node earns the card a token (`designated_defense_token` hook). Round-
         * scoped like the aims it forces — cleared at round end.
         */
        this.designatedNodeId = -1;
        /**
         * The piece the owner placed on this card to denote their implement-time
         * pick — "" when the card takes none. Set once on implement (a CurdleCat
         * copy inherits its target's) and never changes: it gates the card's
         * `ifChoice` passives "for the rest of the game" (Shifty Engineer's
         * scouts-or-bridges free action, catalogue "19"+; Resource Sequencer's
         * scouts-or-mines discount in "19" only).
         */
        this.choice = "";
    }
}
__decorate([
    type("string")
], FortressCardState.prototype, "cardId", void 0);
__decorate([
    type("uint8")
], FortressCardState.prototype, "tokens", void 0);
__decorate([
    type("uint8")
], FortressCardState.prototype, "stored", void 0);
__decorate([
    type("boolean")
], FortressCardState.prototype, "disabled", void 0);
__decorate([
    type("int8")
], FortressCardState.prototype, "capScale", void 0);
__decorate([
    type("boolean")
], FortressCardState.prototype, "copied", void 0);
__decorate([
    type("string")
], FortressCardState.prototype, "copiedCardId", void 0);
__decorate([
    type(["string"])
], FortressCardState.prototype, "markedCardIds", void 0);
__decorate([
    type("boolean")
], FortressCardState.prototype, "markedWinImplemented", void 0);
__decorate([
    type("boolean")
], FortressCardState.prototype, "markedRegularImplemented", void 0);
__decorate([
    type("int8")
], FortressCardState.prototype, "zoneLevel", void 0);
__decorate([
    type(["int16"])
], FortressCardState.prototype, "nodeIds", void 0);
__decorate([
    type(["string"])
], FortressCardState.prototype, "bridgeKeys", void 0);
__decorate([
    type("int16")
], FortressCardState.prototype, "designatedNodeId", void 0);
__decorate([
    type("string")
], FortressCardState.prototype, "choice", void 0);
//# sourceMappingURL=FortressCardState.js.map